<template>
  <base-dialog :value="isShown"
               title="Report a bug"
               text="Describe what went wrong. We include the current page and your session so we can reproduce it."
               variant="form"
               hide-btn
               :is-primary-button-busy="isSubmitting"
               @cancel="handleCancel"
  >
    <template #prompt-input>
      <div class="flex flex-col gap-5 text-left">
        <div class="flex flex-col">
          <label for="bugReportDescription" class="text-sm font-medium text-gray-dark mb-2">
            Description
            <span class="text-focus-error">*</span>
          </label>
          <t-textarea id="bugReportDescription"
                      v-model="description"
                      data-testid="bug-report-description"
                      variant="textSection"
                      rows="4"
                      placeholder="What happened, and what did you expect instead?"
          />
        </div>

        <div class="flex flex-col">
          <span class="text-sm font-medium text-gray-dark mb-2">Severity</span>
          <base-toggle-group v-model="severity"
                             data-testid="bug-report-severity"
                             :tabs="severityTabs"
          />
        </div>

        <div class="flex flex-col">
          <span class="text-sm font-medium text-gray-dark mb-2">Screenshot</span>
          <base-file-input v-model="screenshot"
                           :accept="acceptedScreenshotAccept"
                           data-testid="bug-report-screenshot"
          />
          <span class="mt-2 text-xs text-gray">Optional. PNG, JPG, WebP or GIF up to 5 MB. You can also paste from the clipboard.</span>
          <span v-if="screenshotError" class="mt-1 text-xs text-focus-error">{{ screenshotError }}</span>
        </div>
      </div>
    </template>

    <template #confirm-button>
      <base-button variant="bordered"
                   class="w-full justify-center"
                   :disabled="isSubmitting"
                   @click="handleCancel"
      >
        Cancel
      </base-button>
      <busy-button id="submitBugReportButton"
                   variant="primary"
                   class="w-full ml-4"
                   :is-loading="isSubmitting"
                   :is-disabled="!canSubmit || isSubmitting"
                   data-testid="bug-report-submit"
                   @click="submit"
      >
        Submit report
      </busy-button>
    </template>
  </base-dialog>
</template>

<script>
import BusyButton from '@/components/common/BusyButton'
import BaseButton from '@/components/base-components/BaseButton.vue'
import bugReportService from '@/services/amio/bug-report.service'
import toaster from '@/utils/toaster'
import {
  ACCEPTED_SCREENSHOT_ACCEPT,
  BUG_REPORT_CATEGORY,
  BUG_REPORT_SEVERITIES,
  buildBugReportPayload,
  fileFromClipboard,
  validateScreenshotFile
} from '@/utils/bug-report.utils'

export default {
  name: 'BugReportDialog',

  components: {
    BusyButton,
    BaseButton
  },

  props: {
    isShown: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      description: '',
      severity: 'medium',
      screenshot: null,
      screenshotError: null,
      isSubmitting: false,
      acceptedScreenshotAccept: ACCEPTED_SCREENSHOT_ACCEPT,
      severityTabs: BUG_REPORT_SEVERITIES.map(key => ({
        key,
        label: key.charAt(0).toUpperCase() + key.slice(1)
      }))
    }
  },

  computed: {
    canSubmit() {
      return this.description.trim().length > 0 && !this.screenshotError
    }
  },

  watch: {
    isShown(shown) {
      if (shown) {
        this.resetForm()
        this.bindPasteListener()
      } else {
        this.unbindPasteListener()
      }
    },
    screenshot(file) {
      this.screenshotError = validateScreenshotFile(file)
    }
  },

  methods: {
    resetForm() {
      this.description = ''
      this.severity = 'medium'
      this.screenshot = null
      this.screenshotError = null
      this.isSubmitting = false
    },

    handleCancel() {
      if (this.isSubmitting) return
      this.$emit('cancel')
    },

    bindPasteListener() {
      if (this._pasteBound) return
      this._pasteBound = true
      window.addEventListener('paste', this.handlePaste)
    },

    unbindPasteListener() {
      if (!this._pasteBound) return
      this._pasteBound = false
      window.removeEventListener('paste', this.handlePaste)
    },

    handlePaste(event) {
      if (!this.isShown || this.isSubmitting) return
      const file = fileFromClipboard(event.clipboardData)
      if (!file) return
      event.preventDefault()
      this.screenshot = file
    },

    async submit() {
      if (!this.canSubmit || this.isSubmitting) return

      this.isSubmitting = true
      try {
        const payload = await buildBugReportPayload({
          description: this.description.trim(),
          severity: this.severity,
          screenshotFile: this.screenshot,
          url: window.location.href,
          posthog: this.$posthog
        })

        await bugReportService.submit(payload)

        if (this.$posthog && typeof this.$posthog.capture === 'function') {
          this.$posthog.capture('bug_report_submitted', {
            severity: payload.severity,
            category: payload.category || BUG_REPORT_CATEGORY
          })
        }

        toaster.success('Bug report submitted. Thank you.')
        this.$emit('submitted')
      } catch (err) {
        toaster.error('Could not submit the bug report.', err)
      } finally {
        this.isSubmitting = false
      }
    }
  },

  mounted() {
    if (this.isShown) this.bindPasteListener()
  },

  beforeDestroy() {
    this.unbindPasteListener()
  }
}
</script>
